import { useCallback, useMemo } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSListBox,
  MDSIcon,
  MDSIconKind,
  MDSIconColor,
  MDSLink,
  MDSText,
  MDSTextVariant,
  MDSTextColor,
} from "@/mds";
import { APP_CONFIG } from "@/domains/global/config";
import { windowModule } from "@/modules/window";
import { toastModule } from "@/modules/toast";
import { useRefocus } from "@/domains/react/useRefocus";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { trackEvent } from "@/domains/metrics/segment";
import { useAppStore } from "@/store";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationHeader,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";

export const SettingsZapierPage = observer(() => {
  const { store } = useAppStore();
  const { isEnabled, disable, isDisabling } = store.zapierIntegration;
  const isConnected = isEnabled;

  useRefocus(
    () => {
      store.zapierIntegration.reload();
    },
    { immediate: true }
  );

  const handleDisconnectZapier = useCallback(async () => {
    try {
      await disable();
      trackEvent(TrackedEvent.ZapierDisconnect);
      toastModule.triggerToast({ content: "Disconnected" });
    } catch (_unknownError) {
      toastModule.triggerToast({ content: "Something went wrong. Please try again later." });
    }
  }, [disable]);

  const handleClickZapierIntegrationLink = useCallback(() => {
    trackEvent(TrackedEvent.ZapierIntegrationLinkClick);
    windowModule.openInNewTab({ url: new URL(APP_CONFIG.URLS.ZAPIER_INTEGRATION) });
  }, []);

  const listItems = useMemo(() => {
    if (isEnabled) {
      return [
        {
          id: "1",
          children: (
            <StyledContentListTextContainer>
              <MDSText variant={MDSTextVariant.TextS}>Connected</MDSText>
              <MDSIcon kind={MDSIconKind.CheckCircleSolid} color={MDSIconColor.Positive} />
            </StyledContentListTextContainer>
          ),
          rightAddon: (
            <MDSButton isLoading={isDisabling} onClick={handleDisconnectZapier}>
              Disconnect
            </MDSButton>
          ),
        },
      ];
    }

    return [
      {
        id: "1",
        children: (
          <StyledContentListTextContainer>
            <MDSText variant={MDSTextVariant.TextS}>Not connected</MDSText>
          </StyledContentListTextContainer>
        ),
        rightAddon: (
          <MDSButton onClick={handleClickZapierIntegrationLink}>
            Set up in Zapier
            <MDSIcon kind={MDSIconKind.ArrowUpRight} slot={MDSButtonSlotName.RightIcon} />
          </MDSButton>
        ),
      },
    ];
  }, [isEnabled, isDisabling, handleDisconnectZapier, handleClickZapierIntegrationLink]);

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader
        breadcrumb="Apps and integrations"
        title="Zapier"
        isConnected={isConnected}
      />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <StyledDescriptionCard>
            <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Primary}>
              <MDSLink onPress={handleClickZapierIntegrationLink}>Zapier</MDSLink> lets you connect
              to 8,000 apps without writing a single line of code. A few popular use cases:
            </MDSText>
            <StyledUseCaseList>
              <li>
                Google Drive Sync: Bring documents from specific folders in Google Drive into Mem,
                ensuring your important work is always searchable alongside your notes
              </li>
              <li>
                Task Completion Journal: Track completed tasks from Todoist, Asana, or other task
                managers to maintain a searchable work log
              </li>
              <li>
                Form Response Collector: Capture submissions from Google Forms, Typeform, or other
                survey tools directly in Mem for organized feedback management
              </li>
            </StyledUseCaseList>
          </StyledDescriptionCard>
        </SettingsIntegrationSection>

        <SettingsIntegrationSection>
          <MDSListBox items={listItems} />
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

const StyledDescriptionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.background.card.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing[5]};
  max-width: 700px;
`;

const StyledUseCaseList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing[5]};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-s"]};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StyledContentListTextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
`;
